/**
 * Purpose: Seat-based progression logic for the classroom system.
 * Owns: Advancing seats within a class, detecting class graduation,
 *       determining the current subject for question selection.
 * Notes: Replaces the old linear board path with a per-class seat grid.
 *        Each class has a fixed number of seats (defined in classConfig).
 *        When a player passes the last seat they graduate and move to the
 *        next period. The Final Exam is always the last period (period 9).
 */

import type { Player, ClassPeriod, PlayerClassProgress } from '../types/game';
import type { Category } from '../types/question';
import {
  SUBJECT_CLASS_SIZE,
  FINAL_EXAM,
  FINAL_EXAM_LABEL,
  FINAL_EXAM_PERIOD,
  REGULAR_SUBJECTS,
  TOTAL_PERIODS,
  PURE_CLASSROOM_QUESTIONS,
  PURE_CLASSROOM_FINAL_EXAM_QUESTIONS,
} from '../config/classConfig';
import { shuffleArray } from '../utils/helpers';

// _______________________________________________
//              SEAT ADVANCEMENT
// _______________________________________________

/** Result of advancing a player's seat position within their current class. */
export interface SeatAdvanceResult {
  /** The new seat position after advancing */
  newSeat: number;
  /** Whether the player passed the last seat and graduated the class */
  graduated: boolean;
}

/**
 * Calculate the new seat position after a dice roll.
 * If the new seat exceeds the total seats in the class, the player graduates.
 */
export function advanceSeats(
  currentSeat: number,
  diceRoll: number,
  totalSeats: number,
): SeatAdvanceResult {
  const newSeat = currentSeat + diceRoll;
  const graduated = newSeat >= totalSeats;

  return {
    /* Cap at totalSeats so the UI can show the player on the last seat before graduation */
    newSeat: graduated ? totalSeats : newSeat,
    graduated,
  };
}

// _______________________________________________
//              SCHEDULE GENERATION
// _______________________________________________
// Builds a player's class schedule — 8 regular subjects + Final Exam.

/**
 * Generate a schedule of class periods.
 * If randomized, subjects 1-8 are shuffled; Final Exam is always period 9.
 * If choosing, subjects are kept in the default order from REGULAR_SUBJECTS.
 */
export function generateSchedule(randomize: boolean): ClassPeriod[] {
  const subjects: Category[] = randomize
    ? shuffleArray(REGULAR_SUBJECTS)
    : [...REGULAR_SUBJECTS];

  /* Build the 8 regular class periods */
  const schedule: ClassPeriod[] = subjects.map((subject, index) => ({
    subject,
    classSize: SUBJECT_CLASS_SIZE[subject],
    periodNumber: index + 1,
  }));

  /* Append the Final Exam as period 9 (always last) */
  schedule.push({
    subject: FINAL_EXAM_LABEL,
    classSize: FINAL_EXAM,
    periodNumber: FINAL_EXAM_PERIOD,
  });

  return schedule;
}

// _______________________________________________
//              PURE CLASSROOM SCHEDULE
// _______________________________________________
// In Pure Classroom mode there are no seats or dice. Each period has a fixed
// number of questions determined by the subject. The classSize.seats field
// is set to the question count so existing graduation checks still work.

/**
 * Generate a schedule for Pure Classroom mode.
 * Each period's classSize.seats equals the number of questions for that subject.
 * Grid layout is set to 1×N (single row) since it's purely decorative.
 */
export function generatePureClassroomSchedule(randomize: boolean): ClassPeriod[] {
  const subjects: Category[] = randomize
    ? shuffleArray(REGULAR_SUBJECTS)
    : [...REGULAR_SUBJECTS];

  const schedule: ClassPeriod[] = subjects.map((subject, index) => ({
    subject,
    classSize: {
      seats: PURE_CLASSROOM_QUESTIONS[subject],
      grid: [1, PURE_CLASSROOM_QUESTIONS[subject]],
    },
    periodNumber: index + 1,
  }));

  /* Final Exam always last */
  schedule.push({
    subject: FINAL_EXAM_LABEL,
    classSize: {
      seats: PURE_CLASSROOM_FINAL_EXAM_QUESTIONS,
      grid: [1, PURE_CLASSROOM_FINAL_EXAM_QUESTIONS],
    },
    periodNumber: FINAL_EXAM_PERIOD,
  });

  return schedule;
}

/**
 * Get the total number of questions for a Pure Classroom period.
 * Uses the period's classSize.seats (which stores the question count
 * in Pure Classroom mode).
 */
export function getQuestionsPerPeriod(period: ClassPeriod): number {
  return period.classSize.seats;
}

// _______________________________________________
//              CLASS PROGRESS HELPERS
// _______________________________________________

/** Create empty progress tracking for all periods in a schedule. */
export function createEmptyClassProgress(periodCount: number): PlayerClassProgress[] {
  return Array.from({ length: periodCount }, () => ({
    currentSeat: 0,
    questionsAnswered: 0,
    questionsCorrect: 0,
    questionsWithHelp: 0,
    behaviorDock: 0,
    letterGrade: null,
    passed: false,
  }));
}

/**
 * Get the current class period a player is attending.
 * Returns the ClassPeriod from the player's schedule for their current period.
 */
export function getCurrentClassPeriod(player: Player): ClassPeriod {
  /* Period numbers are 1-indexed; array is 0-indexed */
  const index = player.currentPeriod - 1;
  return player.schedule[index];
}

/**
 * Get the subject for the player's current class period.
 * Used by questionSelector to pull questions from the right category.
 * For the Final Exam, returns null — the question selector should pull
 * from all categories during the Final Exam.
 */
export function getCurrentSubjectForPlayer(player: Player): Category | null {
  const period = getCurrentClassPeriod(player);
  if (period.subject === FINAL_EXAM_LABEL) return null;
  return period.subject;
}

/**
 * Get the class progress entry for a player's current period.
 */
export function getCurrentClassProgress(player: Player): PlayerClassProgress {
  return player.classProgress[player.currentPeriod - 1];
}

/**
 * Check if a player has completed all class periods (including Final Exam).
 */
export function hasPlayerFinished(player: Player): boolean {
  return player.finished;
}

/**
 * Get overall progress percentage for a player across all periods.
 * Considers seats completed in each class relative to total seats across all classes.
 */
export function getPlayerProgress(player: Player): number {
  const totalSeats = player.schedule.reduce(
    (sum, period) => sum + period.classSize.seats,
    0,
  );

  if (totalSeats === 0) return 0;

  const completedSeats = player.classProgress.reduce(
    (sum, progress, index) => {
      if (progress.passed) {
        return sum + player.schedule[index].classSize.seats;
      }
      return sum + progress.currentSeat;
    },
    0,
  );

  return Math.min((completedSeats / totalSeats) * 100, 100);
}

// _______________________________________________
//              EXPORTS FOR BACKWARD COMPATIBILITY
// _______________________________________________
// These constants are re-exported so existing code that imported
// from boardProgression can transition to the new system.

export { REGULAR_SUBJECTS as GAME_SUBJECTS, TOTAL_PERIODS };
